import { useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";

const ITEMS_PER_PAGE = 8;

/* ============================================================
   DepartamentosPage – Layout Profissional Centralizado
   ============================================================ */
export default function DepartamentosPage() {
  const { dark } = useTheme();

  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [modalMode, setModalMode] = useState(null); // "add" | "edit"
  const [editingDepartamento, setEditingDepartamento] = useState(null);
  const [deletingDepartamento, setDeletingDepartamento] = useState(null);
  const [notification, setNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // ══════════ Tokens de cor – fundo realmente escuro no dark ══════════
  const t = dark
    ? {
        bg: "#080c14",
        bgCard: "#111827",
        bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)",
        bgInput: "#1a2232",
        bgDropdown: "#111827",
        border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)",
        textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0",
        textMuted: "#7b93b0",
        textActive: "#00b4ff",
        scrollbar: "rgba(0,174,255,0.2)",
        shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)",
        danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)",
        success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)",
        overlay: "rgba(8,12,20,0.8)",
        glass: true,
      }
    : {
        bg: "#f4f7fc",
        bgCard: "#ffffff",
        bgHover: "#f0f6ff",
        bgActive: "#e6f0ff",
        bgInput: "#fafbfd",
        bgDropdown: "#ffffff",
        border: "#e2e8f0",
        borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e",
        textSecondary: "#475569",
        textMuted: "#8b9eb0",
        textActive: "#0057ff",
        scrollbar: "#cbd5e1",
        shadow: "0 8px 30px rgba(0,50,150,0.06)",
        rowHover: "#f8faff",
        danger: "#ef4444",
        dangerBg: "#fef2f2",
        success: "#16a34a",
        successBg: "#f0fdf4",
        overlay: "rgba(15,23,42,0.4)",
        glass: false,
      };

  // ── Carregar dados ─────────────────────────────────────────────
  const loadDepartamentos = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await Api.getDepartamento();
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
        ? res
        : [];
      setDepartamentos(list);
    } catch {
      setErrorMsg("Não foi possível carregar os departamentos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepartamentos();
  }, [loadDepartamentos]);

  useEffect(() => {
    if (!notification) return;
    const id = setTimeout(() => setNotification(null), 3500);
    return () => clearTimeout(id);
  }, [notification]);

  useEffect(() => setPage(1), [search]);

  // ── Filtro e paginação ────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search) return departamentos;
    const q = search.toLowerCase();
    return departamentos.filter((d) =>
      d.descricao?.toLowerCase().includes(q)
    );
  }, [departamentos, search]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, safePage]);

  // ── Ações ────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingDepartamento(null);
    setModalMode("add");
  };
  const openEdit = (dep) => {
    setEditingDepartamento(dep);
    setModalMode("edit");
  };
  const closeModal = () => {
    setModalMode(null);
    setEditingDepartamento(null);
  };

  const handleSaved = (msg) => {
    closeModal();
    setNotification({ type: "success", text: msg });
    loadDepartamentos();
  };

  const handleDelete = async () => {
    if (!deletingDepartamento) return;
    setActionLoading(true);
    try {
    const response= await Api.deleteDepartamento({ id: deletingDepartamento.id });

if(response.success){
      setNotification({
        type: "success",
        text: `"${deletingDepartamento.descricao}" eliminado.`,
      });
}

if(response.status===409){
    setNotification({
        type: "error",
        text: `Não é possível eliminar o departamento`,
      });
}

      setDeletingDepartamento(null);
      loadDepartamentos();
    } catch {
      setNotification({ type: "error", text: "Erro ao eliminar." });
    } finally {
      setActionLoading(false);
    }
  };

  // ══════════════════════ Estilos CSS ══════════════════════
  const styles = `
    .dep-page {
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      background: ${t.bg};
      min-height: 100vh;
      padding: 32px 20px;
    }
    .dep-container {
      max-width: 1200px;
      margin: 0 auto;
    }
    .dep-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 32px;
      flex-wrap: wrap;
    }
    .dep-heading {
      font-size: 1.65rem;
      font-weight: 700;
      letter-spacing: -0.03em;
      color: ${t.textPrimary};
      margin-bottom: 4px;
    }
    .dep-sub {
      font-size: 0.88rem;
      color: ${t.textMuted};
      font-weight: 400;
    }
    .dep-btn-add {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      height: 44px;
      padding: 0 24px;
      border-radius: 14px;
      border: none;
      background: linear-gradient(135deg, #00aaff, #0044ee);
      color: #fff;
      font-weight: 600;
      font-size: 0.86rem;
      cursor: pointer;
      box-shadow: 0 6px 18px rgba(0,100,255,0.3);
      transition: all 0.25s;
      letter-spacing: -0.01em;
    }
    .dep-btn-add:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 26px rgba(0,100,255,0.45);
    }
    .dep-btn-add:active { transform: translateY(0); }

    .dep-toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;
      flex-wrap: wrap;
    }
    .dep-search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      background: ${t.bgCard};
      border: 1px solid ${t.border};
      border-radius: 14px;
      padding: 0 16px;
      height: 44px;
      width: 300px;
      transition: border 0.2s, box-shadow 0.2s;
    }
    .dep-search-box:focus-within {
      border-color: #0090e7;
      box-shadow: 0 0 0 3px rgba(0,144,231,0.18);
    }
    .dep-search-box input {
      background: none; border: none; outline: none;
      font-size: 0.86rem; color: ${t.textPrimary}; width: 100%;
    }
    .dep-search-box input::placeholder { color: ${t.textMuted}; }
    .dep-search-icon { color: ${t.textMuted}; display: flex; flex-shrink: 0; }

    .dep-table-card {
      background: ${t.bgCard};
      border: 1px solid ${t.border};
      border-radius: 18px;
      overflow: hidden;
    }
    .dep-table {
      width: 100%; border-collapse: collapse;
    }
    .dep-table thead th {
      text-align: left; font-size: 0.7rem; font-weight: 700;
      letter-spacing: 0.08em; text-transform: uppercase;
      color: ${t.textMuted}; padding: 14px 22px;
      background: ${t.bgHover}; border-bottom: 1px solid ${t.border};
      white-space: nowrap;
    }
    .dep-table tbody td {
      padding: 14px 22px; font-size: 0.88rem;
      color: ${t.textPrimary}; border-bottom: 1px solid ${t.border};
      vertical-align: middle;
    }
    .dep-table tbody tr:last-child td { border-bottom: none; }
    .dep-table tbody tr { transition: background 0.15s; }
    .dep-table tbody tr:hover { background: ${t.rowHover}; }

    .dep-actions {
      display: flex; align-items: center; gap: 6px; justify-content: flex-end;
    }
    .dep-icon-btn {
      width: 36px; height: 36px; border-radius: 10px;
      border: 1px solid ${t.border}; background: transparent;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; color: ${t.textSecondary};
      transition: all 0.2s;
    }
    .dep-icon-btn:hover { background: ${t.bgHover}; color: ${t.textActive}; border-color: ${t.borderActive}; }
    .dep-icon-btn.danger:hover { background: ${t.dangerBg}; color: ${t.danger}; border-color: ${t.danger}; }

    .dep-pagination {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 22px; border-top: 1px solid ${t.border};
      color: ${t.textSecondary}; font-size: 0.84rem;
    }
    .dep-pagination-controls { display: flex; align-items: center; gap: 4px; }
    .dep-pagination-btn {
      width: 34px; height: 34px; border-radius: 8px;
      border: 1px solid ${t.border}; background: ${t.bgCard};
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; color: ${t.textSecondary}; font-weight: 500;
      transition: all 0.2s;
    }
    .dep-pagination-btn:hover:not(:disabled) { background: ${t.bgHover}; color: ${t.textActive}; border-color: ${t.borderActive}; }
    .dep-pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .dep-pagination-num {
      width: 34px; height: 34px; border-radius: 8px; border: none;
      background: transparent; color: ${t.textSecondary}; cursor: pointer;
      font-weight: 500; transition: all 0.2s;
    }
    .dep-pagination-num.active {
      background: ${t.bgActive}; color: ${t.textActive}; font-weight: 700;
    }
    .dep-pagination-num:hover:not(.active) { background: ${t.bgHover}; color: ${t.textPrimary}; }

    /* Skeleton loading */
    .dep-skeleton-row {
      display: flex; align-items: center; padding: 14px 22px;
      border-bottom: 1px solid ${t.border};
    }
    .dep-skeleton-line {
      height: 12px; border-radius: 6px; background: ${t.bgHover};
      animation: pulse 1.6s infinite; margin-right: 20px;
    }
    .dep-skeleton-line.short { width: 90px; }
    .dep-skeleton-line.long { width: 200px; }
    @keyframes pulse {
      0% { opacity: 0.5; } 50% { opacity: 0.8; } 100% { opacity: 0.5; }
    }

    .dep-error-banner {
      background: ${t.dangerBg}; color: ${t.danger};
      border: 1px solid rgba(239,68,68,0.25); border-radius: 14px;
      padding: 14px 20px; font-size: 0.85rem; margin-bottom: 24px;
      display: flex; align-items: center; gap: 10px;
    }

    /* Modal */
    .dep-overlay {
      position: fixed; inset: 0; background: ${t.overlay};
      backdrop-filter: blur(6px); z-index: 800;
      display: flex; align-items: center; justify-content: center;
      padding: 20px; animation: depFadeIn 0.2s ease;
    }
    @keyframes depFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .dep-modal {
      background: ${t.bgDropdown}; border: 1px solid ${t.border};
      border-radius: 22px; box-shadow: ${t.shadow};
      width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto;
      animation: depModalIn 0.25s cubic-bezier(.22,1,.36,1);
    }
    @keyframes depModalIn {
      from { opacity: 0; transform: translateY(-12px) scale(0.96); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .dep-modal-header {
      padding: 24px 28px 20px; border-bottom: 1px solid ${t.border};
      display: flex; align-items: center; justify-content: space-between;
    }
    .dep-modal-title { font-size: 1.15rem; font-weight: 700; color: ${t.textPrimary}; letter-spacing: -0.02em; }
    .dep-modal-sub { font-size: 0.8rem; color: ${t.textMuted}; margin-top: 4px; }
    .dep-modal-close {
      width: 32px; height: 32px; border-radius: 10px; border: none;
      background: ${t.bgHover}; color: ${t.textSecondary};
      display: flex; align-items: center; justify-content: center; cursor: pointer;
    }
    .dep-modal-close:hover { background: ${t.bgActive}; color: ${t.textActive}; }
    .dep-modal-body { padding: 22px 28px; display: flex; flex-direction: column; gap: 18px; }
    .dep-field { display: flex; flex-direction: column; gap: 6px; }
    .dep-field label { font-size: 0.78rem; font-weight: 600; color: ${t.textSecondary}; }
    .dep-field input {
      height: 44px; border-radius: 12px; border: 1px solid ${t.border};
      background: ${t.bgInput}; color: ${t.textPrimary};
      padding: 0 16px; font-size: 0.86rem; outline: none;
      transition: border 0.2s, box-shadow 0.2s;
    }
    .dep-field input:focus {
      border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.15);
    }
    .dep-field-error { font-size: 0.74rem; color: ${t.danger}; }
    .dep-modal-footer {
      padding: 20px 28px 24px; display: flex; gap: 14px; justify-content: flex-end;
      border-top: 1px solid ${t.border};
    }
    .dep-btn-secondary {
      height: 42px; padding: 0 22px; border-radius: 12px;
      border: 1px solid ${t.border}; background: transparent;
      color: ${t.textSecondary}; font-weight: 600; font-size: 0.84rem;
      cursor: pointer; transition: all 0.2s;
    }
    .dep-btn-secondary:hover { background: ${t.bgHover}; color: ${t.textPrimary}; }
    .dep-btn-primary {
      height: 42px; padding: 0 24px; border-radius: 12px; border: none;
      background: linear-gradient(135deg, #00aaff, #0044ee);
      color: #fff; font-weight: 600; font-size: 0.84rem; cursor: pointer;
      display: flex; align-items: center; gap: 8px;
      box-shadow: 0 4px 14px rgba(0,100,255,0.3);
      transition: all 0.2s;
    }
    .dep-btn-primary:hover:not(:disabled) {
      box-shadow: 0 6px 18px rgba(0,100,255,0.45); transform: translateY(-1px);
    }
    .dep-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
    .dep-btn-danger {
      height: 42px; padding: 0 24px; border-radius: 12px; border: none;
      background: ${t.danger}; color: #fff; font-weight: 600; font-size: 0.84rem;
      cursor: pointer; transition: all 0.2s;
    }
    .dep-btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
    .mini-spinner {
      display: inline-block; width: 16px; height: 16px;
      border-radius: 50%; border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff; animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    .dep-delete-body {
      padding: 26px 28px; font-size: 0.9rem;
      color: ${t.textSecondary}; line-height: 1.6;
    }
    .dep-delete-body strong { color: ${t.textPrimary}; }

    .dep-toast {
      position: fixed; bottom: 28px; right: 28px; z-index: 900;
      display: flex; align-items: center; gap: 12px;
      padding: 16px 22px; border-radius: 16px;
      background: ${t.bgDropdown}; border: 1px solid ${t.border};
      box-shadow: ${t.shadow}; font-size: 0.86rem; font-weight: 500;
      color: ${t.textPrimary};
      animation: toastIn 0.3s ease;
    }
    @keyframes toastIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    .dep-toast-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }

    @media (max-width: 640px) {
      .dep-table thead { display: none; }
      .dep-table tbody tr {
        display: flex; flex-direction: column; gap: 8px;
        padding: 16px 18px;
      }
      .dep-table tbody td { padding: 0; border-bottom: none; }
      .dep-actions { justify-content: flex-start; margin-top: 6px; }
      .dep-search-box { width: 100%; }
      .dep-pagination { flex-direction: column; gap: 10px; }
    }
  `;

  return (
    <div className="dep-page">
      <style>{styles}</style>
      <div className="dep-container">
        {/* Header */}
        <div className="dep-header">
          <div>
            <h1 className="dep-heading">Departamentos</h1>
            <p className="dep-sub">Gerir departamentos da organização</p>
          </div>
          <button className="dep-btn-add" onClick={openAdd}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Novo departamento
          </button>
        </div>

        {errorMsg && (
          <div className="dep-error-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {errorMsg}
          </div>
        )}

        {/* Toolbar */}
        <div className="dep-toolbar">
          <div className="dep-search-box">
            <span className="dep-search-icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input
              placeholder="Pesquisar departamento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table / Skeleton */}
        <div className="dep-table-card">
          {loading ? (
            <div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="dep-skeleton-row">
                  <div className="dep-skeleton-line long" />
                  <div style={{ flex: 1 }} />
                  <div className="dep-skeleton-line short" />
                  <div className="dep-skeleton-line short" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "70px 20px", textAlign: "center", color: t.textMuted }}>
              <div style={{ fontSize: "1rem", fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>
                Nenhum departamento encontrado
              </div>
              <div style={{ fontSize: "0.85rem" }}>
                Ajuste a pesquisa ou crie um novo departamento.
              </div>
            </div>
          ) : (
            <>
              <table className="dep-table">
                <thead>
                  <tr>
                    <th>Descrição</th>
                     
                    <th style={{ textAlign: "right" }}>Acções</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((dep) => (
                    <tr key={dep.id}>
                      <td style={{ fontWeight: 600 }}>{dep.descricao}</td>
              
                      <td>
                        <div className="dep-actions">
                          <button
                            className="dep-icon-btn"
                            title="Editar"
                            onClick={() => openEdit(dep)}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
                          </button>
                          <button
                            className="dep-icon-btn danger"
                            title="Eliminar"
                            onClick={() => setDeletingDepartamento(dep)}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Paginação */}
              <div className="dep-pagination">
                <span>
                  Mostrando {paginated.length} de {filtered.length} departamentos
                </span>
                <div className="dep-pagination-controls">
                  <button
                    className="dep-pagination-btn"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      className={`dep-pagination-num ${num === safePage ? "active" : ""}`}
                      onClick={() => setPage(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className="dep-pagination-btn"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modais */}
      {modalMode && (
        <DepartamentoFormModal
          mode={modalMode}
          departamento={editingDepartamento}
          t={t}
          onClose={closeModal}
          onSaved={handleSaved}
          actionLoading={actionLoading}
          setActionLoading={setActionLoading}
        />
      )}
      {deletingDepartamento && (
        <DeleteConfirmModal
          departamento={deletingDepartamento}
          t={t}
          onCancel={() => setDeletingDepartamento(null)}
          onConfirm={handleDelete}
          deleting={actionLoading}
        />
      )}

      {/* Toast */}
      {notification && (
        <div className="dep-toast">
          <span
            className="dep-toast-dot"
            style={{
              background:
                notification.type === "success" ? t.success : t.danger,
            }}
          />
          {notification.text}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════ MODAL FORMULÁRIO ════════════════════════ */
function DepartamentoFormModal({
  mode,
  departamento,
  t,
  onClose,
  onSaved,
  actionLoading,
  setActionLoading,
}) {
  const isEdit = mode === "edit";
  const [descricao, setDescricao] = useState(departamento?.descricao ?? "");
  const [error, setError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const validate = () => {
    if (!descricao.trim()) {
      setError("Descrição obrigatória.");
      return false;
    }
    if (descricao.trim().length > 255) {
      setError("Máximo 255 caracteres.");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setActionLoading(true);
    setSubmitError("");

    try {
      if (isEdit) {
        await Api.updateDepartamento({
          id: departamento.id,
          descricao: descricao.trim(),
        });
        onSaved(`Departamento "${descricao.trim()}" atualizado.`);
      } else {
        await Api.addDepartamento({ descricao: descricao.trim() });
        onSaved(`Departamento "${descricao.trim()}" criado.`);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao guardar departamento.";
      setSubmitError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div
      className="dep-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dep-modal">
        <div className="dep-modal-header">
          <div>
            <div className="dep-modal-title">
              {isEdit ? "Editar departamento" : "Novo departamento"}
            </div>
            <div className="dep-modal-sub">
              {isEdit
                ? "Actualize a descrição do departamento."
                : "Crie um novo departamento na organização."}
            </div>
          </div>
          <button className="dep-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="dep-modal-body">
            {submitError && <div className="dep-error-banner">{submitError}</div>}
            <div className="dep-field">
              <label>Descrição</label>
              <input
                value={descricao}
                onChange={(e) => {
                  setDescricao(e.target.value);
                  if (error) setError("");
                }}
                placeholder="Ex: Engenharia"
                autoFocus
              />
              {error && <span className="dep-field-error">{error}</span>}
            </div>
          </div>
          <div className="dep-modal-footer">
            <button type="button" className="dep-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="dep-btn-primary"
              disabled={actionLoading}
            >
              {actionLoading ? <span className="mini-spinner" /> : null}
              {isEdit ? "Guardar alterações" : "Criar departamento"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ════════════════════════ MODAL ELIMINAR ════════════════════════ */
function DeleteConfirmModal({ departamento, t, onCancel, onConfirm, deleting }) {
  return (
    <div
      className="dep-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="dep-modal" style={{ maxWidth: "420px" }}>
        <div className="dep-modal-header">
          <div className="dep-modal-title">Eliminar departamento</div>
          <button className="dep-modal-close" onClick={onCancel}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="dep-delete-body">
          Tem a certeza que deseja eliminar{" "}
          <strong>{departamento.descricao}</strong>? Esta ação é irreversível.
        </div>
        <div className="dep-modal-footer">
          <button className="dep-btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button
            className="dep-btn-danger"
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}